import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Trash2, Loader2, Search, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useNhrplCache } from '../hooks/useCodeCache';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

interface NhrplOverride {
  code: string;
  tariffAmount: number;
  description: string | null;
  reason: string;
  baseTariffAmount: number | null;
  updatedAt?: string;
  updatedBy?: string;
}

interface OverrideFormState {
  code: string;
  tariffAmount: string;
  description: string;
  reason: string;
}

const emptyForm: OverrideFormState = {
  code: '',
  tariffAmount: '0',
  description: '',
  reason: '',
};

export function NhrplOverridesPage() {
  const { get, post, patch, del } = useApi();
  const nhrplCache = useNhrplCache();

  const [overrides, setOverrides] = useState<NhrplOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [editOverride, setEditOverride] = useState<NhrplOverride | null>(null);
  const [deleteCode, setDeleteCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<OverrideFormState>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get<NhrplOverride[]>('/nhrpl/overrides');
      if (res.success) {
        setOverrides(res.data || []);
      } else {
        setError(res.error || 'Failed to load overrides');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load overrides');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return overrides;
    return overrides.filter(
      (o) =>
        o.code.toLowerCase().includes(q) ||
        (o.description || '').toLowerCase().includes(q) ||
        (o.reason || '').toLowerCase().includes(q)
    );
  }, [overrides, search]);

  const openAdd = () => {
    setForm(emptyForm);
    setEditOverride(null);
    setShowAddModal(true);
  };

  const openEdit = (ov: NhrplOverride) => {
    setEditOverride(ov);
    setForm({
      code: ov.code,
      tariffAmount: String(ov.tariffAmount ?? 0),
      description: ov.description || '',
      reason: ov.reason || '',
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditOverride(null);
    setForm(emptyForm);
  };

  const submit = useCallback(async () => {
    if (!form.code.trim()) {
      setError('Code is required');
      return;
    }
    const tariff = parseFloat(form.tariffAmount);
    if (isNaN(tariff) || tariff < 0) {
      setError('Tariff must be a non-negative number');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (editOverride) {
        const res = await patch(`/nhrpl/overrides/${encodeURIComponent(editOverride.code)}`, {
          tariffAmount: tariff,
          description: form.description.trim() || null,
          reason: form.reason.trim(),
        });
        if (!res.success) throw new Error(res.error || 'Failed to update');
      } else {
        const res = await post('/nhrpl/overrides', {
          code: form.code.trim(),
          tariffAmount: tariff,
          description: form.description.trim() || null,
          reason: form.reason.trim(),
        });
        if (!res.success) throw new Error(res.error || 'Failed to create');
      }
      closeModal();
      nhrplCache.invalidate();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to save override');
    } finally {
      setSubmitting(false);
    }
  }, [form, editOverride, patch, post, load, nhrplCache]);

  const confirmDelete = useCallback(async () => {
    if (!deleteCode) return;
    setSubmitting(true);
    try {
      const res = await del(`/nhrpl/overrides/${encodeURIComponent(deleteCode)}`);
      if (!res.success) throw new Error(res.error || 'Failed to delete');
      setDeleteCode(null);
      nhrplCache.invalidate();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to delete override');
    } finally {
      setSubmitting(false);
    }
  }, [deleteCode, del, load, nhrplCache]);

  return (
    <div className="p-6">
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Practice-specific price overrides for NHRPL codes. Overrides replace the GEMS tariff
          wherever the code is used — useful for consultation codes GEMS doesn't price (e.g. 0190),
          or where the practice bills at a different rate. Changes take effect immediately.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm flex items-start gap-2">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by code, description, or reason..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm"
          />
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          <Plus size={16} /> Add override
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading overrides...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500 text-sm">
          {overrides.length === 0
            ? 'No overrides configured yet. Add one to override a GEMS tariff for this practice.'
            : 'No overrides match your search.'}
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2">Override (R)</th>
                <th className="text-right px-3 py-2">GEMS base (R)</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ov) => (
                <tr key={ov.code} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono">{ov.code}</td>
                  <td className="px-3 py-2">{ov.description || <span className="text-gray-400 italic">—</span>}</td>
                  <td className="px-3 py-2 text-right font-medium">{ov.tariffAmount.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-gray-500">
                    {ov.baseTariffAmount !== null && ov.baseTariffAmount !== undefined
                      ? ov.baseTariffAmount.toFixed(2)
                      : <span className="text-gray-400 italic">no base</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{ov.reason || <span className="text-gray-400 italic">—</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openEdit(ov)}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Edit"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => setDeleteCode(ov.code)}
                      className="text-red-600 hover:text-red-800 p-1 ml-1"
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">
              {editOverride ? `Edit override — ${editOverride.code}` : 'Add override'}
            </h2>

            <div className="space-y-3">
              {!editOverride && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="e.g. 0190"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Tariff (R) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.tariffAmount}
                  onChange={(e) => setForm({ ...form, tariffAmount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Description <span className="text-gray-400">(optional — overrides GEMS description)</span>
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Leave blank to keep GEMS description"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Reason</label>
                <textarea
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="e.g. GEMS TRF040 does not price this code; practice tariff"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeModal}
                disabled={submitting}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {editOverride ? 'Save changes' : 'Add override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteCode && (
        <DeleteConfirmModal
          title="Remove override?"
          description={`The override for ${deleteCode} will be deleted and the price will revert to the GEMS base tariff (or disappear entirely if GEMS does not price it).`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteCode(null)}
        />
      )}
    </div>
  );
}
